import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import { useBranding } from '../hooks/useBranding';
import MetricCard from './MetricCard';
import StandardHeader from './layout/StandardHeader';
import { AIActionDropdown } from './common/AIActionsComponent';
import FinanceEntryModal from './FinanceEntryModal';
import { useAuth } from '../context/AuthContext';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  ArrowUpRight,
  Layers,
  Search,
  Filter,
  Plus,
  ArrowDownRight,
  PieChart as PieIcon,
  Activity,
  CheckCircle2,
  AlertCircle,
  Clock,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useNavigation } from '../context/NavigationContext';
import { apiClient } from '../api/client';
import LoadingState from './ui/LoadingState';
import ErrorState from './ui/ErrorState';
import EmptyState from './ui/EmptyState';

const COLORS = ['#0F172A', '#334155', '#475569', '#64748B', '#94A3B8'];
const asList = (value: any): any[] => {
  const data = value?.data ?? value;
  return Array.isArray(data) ? data : [];
};

export default function FinanceiroView() {
  const { activeWorkspace } = useAuth();
  const { iconUrl, appName } = useBranding();
  const { globalFilters } = useNavigation();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'TODOS' | 'RECEITA' | 'DESPESA' | 'PAGAS' | 'DIVIDAS' | 'RECEBIDAS' | 'A_RECEBER'>('TODOS');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<string>('30d');

  const workspaceId = activeWorkspace?.id;

  const { data: overviewResponse, isLoading: overviewLoading, error: overviewError, refetch: refetchOverview } = useQuery({
    queryKey: ['finance-overview', workspaceId, periodFilter],
    queryFn: () => apiClient.get('/finance/overview', { params: { period: periodFilter } }).then((r) => r.data),
    enabled: !!workspaceId,
  });

  const { data: invoices, isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery({
    queryKey: ['invoices', workspaceId, statusFilter],
    queryFn: () => apiClient.get('/invoices', { params: { status: statusFilter } }).then((r) => r.data),
    enabled: !!workspaceId,
  });

  const { data: transactions, isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ['transactions', workspaceId],
    queryFn: () => apiClient.get('/transactions').then((r) => r.data),
    enabled: !!workspaceId,
  });

  const { data: accounts, isLoading: accountsLoading, refetch: refetchAccounts } = useQuery({
    queryKey: ['accounts', workspaceId],
    queryFn: () => apiClient.get('/accounts').then((r) => r.data),
    enabled: !!workspaceId,
  });

  const { data: expenses, isLoading: expensesLoading, refetch: refetchExpenses } = useQuery({
    queryKey: ['expenses', workspaceId],
    queryFn: () => apiClient.get('/expenses').then((r) => r.data),
    enabled: !!workspaceId,
  });

  const { data: revenues, isLoading: revenuesLoading, refetch: refetchRevenues } = useQuery({
    queryKey: ['revenues', workspaceId],
    queryFn: () => apiClient.get('/revenues').then((r) => r.data),
    enabled: !!workspaceId,
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/invoices', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchInvoices();
      refetchOverview();
    },
  });

  const createTransactionMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/transactions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchTransactions();
      refetchOverview();
    },
  });

  const createExpenseMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/expenses', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchExpenses();
      refetchOverview();
    },
  });

  const createRevenueMutation = useMutation({
    mutationFn: (data: any) => apiClient.post('/revenues', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchRevenues();
      refetchOverview();
    },
  });

  const updateInvoiceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/invoices/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchInvoices();
      refetchOverview();
    },
  });

  const updateExpenseMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/expenses/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchExpenses();
      refetchOverview();
    },
  });

  const updateRevenueMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => apiClient.put(`/revenues/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchRevenues();
      refetchOverview();
    },
  });

  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/invoices/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchInvoices();
      refetchOverview();
    },
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/expenses/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchExpenses();
      refetchOverview();
    },
  });

  const deleteRevenueMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/revenues/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenues'] });
      queryClient.invalidateQueries({ queryKey: ['finance-overview'] });
      refetchRevenues();
      refetchOverview();
    },
  });

  const isLoading = overviewLoading || invoicesLoading || transactionsLoading || accountsLoading || expensesLoading || revenuesLoading;
  const hasError = overviewError;

  const entries = useMemo(() => {
    if (!invoices && !transactions && !expenses && !revenues) return [];
    const all: any[] = [];
    asList(invoices).forEach((inv: any) => {
      all.push({
        id: inv.id,
        entityType: 'invoice',
        type: 'RECEITA',
        status: inv.status,
        amount: Number(inv.total || 0),
        description: `Fatura ${inv.invoiceNumber}`,
        category: 'Fatura',
        companyId: inv.customerId,
        company: inv.Customer?.name || '-',
        date: inv.issuedAt || inv.createdAt,
      });
    });
    asList(transactions).forEach((tx: any) => {
      all.push({
        id: tx.id,
        entityType: 'transaction',
        type: tx.type === 'CREDIT' ? 'RECEITA' : 'DESPESA',
        status: 'PAGO',
        amount: Number(tx.amount || 0),
        description: tx.description || '-',
        category: tx.Account?.name || 'Transação',
        companyId: tx.accountId,
        company: tx.Account?.name || '-',
        date: tx.occurredAt,
      });
    });
    asList(expenses).forEach((exp: any) => {
      all.push({
        id: exp.id,
        entityType: 'expense',
        type: 'DESPESA',
        status: 'PAGO',
        amount: Number(exp.amount || 0),
        description: exp.description || '-',
        category: exp.category || 'Despesa',
        companyId: exp.accountId,
        company: exp.Account?.name || '-',
        date: exp.occurredAt,
      });
    });
    asList(revenues).forEach((rev: any) => {
      all.push({
        id: rev.id,
        entityType: 'revenue',
        type: 'RECEITA',
        status: 'PAGO',
        amount: Number(rev.amount || 0),
        description: rev.description || '-',
        category: rev.category || 'Receita',
        companyId: rev.accountId,
        company: rev.Account?.name || '-',
        date: rev.occurredAt,
      });
    });
    return all;
  }, [invoices, transactions, expenses, revenues]);

  const overview = useMemo(() => {
    const api = overviewResponse?.data ?? overviewResponse ?? {};
    const revenueTotal = asList(revenues).reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const expenseTotal = asList(expenses).reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const invoiceTotal = asList(invoices).reduce((sum, item) => sum + Number(item.total || 0), 0);
    return {
      ...api,
      totalRevenue: Number(api.totalRevenue || 0) || revenueTotal || invoiceTotal,
      totalExpenses: Number(api.totalExpenses || 0) || expenseTotal,
      netProfit: Number(api.netProfit || 0) || (revenueTotal - expenseTotal),
      recentInvoices: asList(api.recentInvoices).length ? asList(api.recentInvoices) : asList(invoices),
    };
  }, [overviewResponse, revenues, expenses, invoices]);

  const chartData = useMemo(() => {
    if (!overview?.recentInvoices) return [];
    const monthly: Record<string, number> = {};
    overview.recentInvoices.forEach((inv: any) => {
      const d = new Date(inv.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthly[key] = (monthly[key] || 0) + Number(inv.total || 0);
    });
    return Object.entries(monthly)
      .map(([key, value]) => ({ name: key, value }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [overview]);

  const companyData = useMemo(() => {
    const byCompany: Record<string, number> = {};
    entries.forEach((e) => {
      if (e.type === 'RECEITA') {
        byCompany[e.company || 'Outros'] = (byCompany[e.company || 'Outros'] || 0) + e.amount;
      }
    });
    return Object.entries(byCompany)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [entries]);

  const filteredEntries = useMemo(() => {
    let result = entries;
    if (globalFilters.companyId) {
      result = result.filter((e: any) => e.companyId === globalFilters.companyId);
    }
    if (activeTab === 'RECEITA') result = result.filter((e: any) => e.type === 'RECEITA');
    else if (activeTab === 'DESPESA') result = result.filter((e: any) => e.type === 'DESPESA');
    else if (activeTab === 'PAGAS') result = result.filter((e: any) => e.type === 'DESPESA' && e.status === 'PAID');
    else if (activeTab === 'DIVIDAS') result = result.filter((e: any) => e.type === 'DESPESA' && e.status !== 'PAID');
    else if (activeTab === 'RECEBIDAS') result = result.filter((e: any) => e.type === 'RECEITA' && e.status === 'PAID');
    else if (activeTab === 'A_RECEBER') result = result.filter((e: any) => e.type === 'RECEITA' && e.status !== 'PAID');
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter((e: any) => (e.description || '').toLowerCase().includes(q) || (e.category || '').toLowerCase().includes(q) || (e.company || '').toLowerCase().includes(q));
    }
    return result.sort((a: any, b: any) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime());
  }, [entries, activeTab, searchTerm, globalFilters.companyId]);

  const formatCurrency = (val: number) => `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleExportPDF = async () => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const now = new Date();
      const formattedDate = now.toLocaleDateString('pt-BR') + ' às ' + now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      let titleX = 20;
      if (iconUrl) {
        try {
          const base64Img = await new Promise<string>((resolve, reject) => {
            const img = new Image();
            img.setAttribute('crossOrigin', 'anonymous');
            img.src = iconUrl;
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth || img.width;
              canvas.height = img.naturalHeight || img.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                resolve(canvas.toDataURL('image/png'));
              } else {
                reject(new Error('Canvas context is null'));
              }
            };
            img.onerror = reject;
          });
          doc.addImage(base64Img, 'PNG', 20, 18, 11, 11);
          titleX = 35;
        } catch {
          doc.setFillColor(15, 23, 42);
          doc.roundedRect(20, 18, 11, 11, 2.5, 2.5, 'F');
          doc.setFillColor(59, 130, 246);
          doc.rect(20.5, 18.5, 1.2, 1.2, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.text((appName || 'C').charAt(0).toUpperCase(), 25.5, 26, { align: 'center' });
          titleX = 35;
        }
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(15, 23, 42);
      doc.text((appName || 'CYZOR CONTROL').toUpperCase(), titleX, 25);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text('Relatório Executivo de Saúde Financeira & Transações', titleX, 29.5);
      doc.setFontSize(8);
      doc.text(`Workspace: ${activeWorkspace?.name || 'Geral'}`, 190, 25, { align: 'right' });
      doc.text(`Gerado em: ${formattedDate}`, 190, 30, { align: 'right' });
      doc.setDrawColor(241, 245, 249);
      doc.setLineWidth(0.8);
      doc.line(20, 37, 190, 37);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('Resumo Executivo de Indicadores', 20, 48);
      const kpis = [
        { label: 'RECEITA MENSAL', val: formatCurrency(Number(overview?.totalRevenue || 0)), color: [16, 185, 129] },
        { label: 'RECEITA ANUAL', val: formatCurrency(Number(overview?.totalRevenue || 0)), color: [15, 23, 42] },
        { label: 'CUSTOS MENSAIS', val: formatCurrency(Number(overview?.totalExpenses || 0)), color: [225, 29, 72] },
        { label: 'LUCRO LÍQUIDO', val: formatCurrency(Number(overview?.netProfit || 0)), color: [37, 99, 235] },
        { label: 'FATURAS ABERTAS', val: String(overview?.overdueInvoices || 0), color: [71, 85, 105] },
      ];
      let xOffset = 20;
      kpis.forEach((kpi) => {
        doc.setFillColor(250, 250, 251);
        doc.roundedRect(xOffset, 53, 31, 18, 3, 3, 'F');
        doc.setFillColor(kpi.color[0], kpi.color[1], kpi.color[2]);
        doc.rect(xOffset, 53, 31, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
        doc.setTextColor(100, 116, 139);
        doc.text(kpi.label, xOffset + 2, 59);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8.5);
        doc.setTextColor(15, 23, 42);
        doc.text(kpi.val, xOffset + 2, 66);
        xOffset += 35;
      });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text('Demonstrativo de Lançamentos', 20, 85);
      const printTableHeader = (doc: any, currentY: number) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text('DATA', 20, currentY);
        doc.text('DESCRIÇÃO', 40, currentY);
        doc.text('CATEGORIA', 100, currentY);
        doc.text('EMPRESA', 135, currentY);
        doc.text('VALOR', 170, currentY, { align: 'right' });
        doc.text('STATUS', 190, currentY, { align: 'right' });
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.4);
        doc.line(20, currentY + 2.5, 190, currentY + 2.5);
        return currentY + 7.5;
      };
      let currentY = 92;
      currentY = printTableHeader(doc, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      filteredEntries.forEach((row: any, index: number) => {
        if (currentY > 270) {
          doc.addPage();
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.setTextColor(15, 23, 42);
          doc.text('CYZOR CONTROL - Relatório de Transações (Cont.)', 20, 20);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(148, 163, 184);
          doc.text(`Página ${doc.getNumberOfPages()}`, 190, 20, { align: 'right' });
          doc.setDrawColor(241, 245, 249);
          doc.setLineWidth(0.5);
          doc.line(20, 23, 190, 23);
          currentY = printTableHeader(doc, 28);
        }
        const isPositive = row.type === 'RECEITA';
        const dateStr = new Date(row.date || Date.now()).toLocaleDateString('pt-BR');
        const descStr = row.description || '-';
        const categoryStr = row.category || 'Geral';
        const companyStr = row.company || '-';
        const amountStr = (isPositive ? '+ ' : '- ') + formatCurrency(Number(row.amount));
        if (index % 2 === 0) {
          doc.setFillColor(252, 252, 253);
          doc.rect(20, currentY - 5.5, 170, 7.5, 'F');
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(15, 23, 42);
        doc.text(dateStr, 20, currentY);
        doc.setFont('helvetica', 'normal');
        doc.text(descStr.length > 28 ? descStr.substring(0, 26) + '...' : descStr, 40, currentY);
        doc.text(categoryStr.length > 15 ? categoryStr.substring(0, 13) + '...' : categoryStr, 100, currentY);
        doc.text(companyStr.length > 18 ? companyStr.substring(0, 16) + '...' : companyStr, 135, currentY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(isPositive ? 16 : 225, isPositive ? 185 : 29, isPositive ? 129 : 72);
        doc.text(amountStr, 170, currentY, { align: 'right' });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(16, 185, 129);
        doc.text('Liquidado', 190, currentY, { align: 'right' });
        currentY += 7.5;
      });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('Cyzor Control - Gestão Corporativa Avançada de Alta Performance', 105, 288, { align: 'center' });
      doc.save(`relatorio_financeiro_${activeWorkspace?.name?.toLowerCase().replace(/\s+/g, '_') || 'workspace'}.pdf`);
    } catch (err) {
      console.error('Failed to generate PDF report:', err);
      alert('Houve um erro ao gerar o relatório em PDF. Por favor, tente novamente.');
    }
  };

  if (isLoading && !overview) {
    return <LoadingState message="Carregando dados financeiros..." />;
  }

  if (hasError) {
    return <ErrorState message="Erro ao carregar dados financeiros" retry={() => refetchOverview()} />;
  }

  return (
    <div className="w-full mx-auto pb-12 flex flex-col gap-8 animate-in fade-in duration-500 relative text-left px-4 sm:px-6 lg:px-10">
      <StandardHeader
        title="Financeiro"
        subtitle="Gestão executiva de saúde financeira, fluxo de caixa, DRE sintética e consolidação orçamentária."
        actions={[
          {
            label: 'Exportar PDF',
            onClick: handleExportPDF,
            variant: 'secondary',
          },
          {
            label: 'Novo Lançamento',
            icon: Plus,
            onClick: () => setIsModalOpen(true),
            variant: 'primary',
          },
        ]}
      >
        <AIActionDropdown entityId="finance" actions={['createFinancialPlan']} />
      </StandardHeader>

      <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Receita Mensal"
          value={formatCurrency(Number(overview?.totalRevenue || 0))}
          sub="Recebido no mês atual"
          icon={DollarSign}
          trend="+12%"
          trendUp={true}
          bg="bg-emerald-50/80"
          color="text-emerald-700"
          secondaryLabel="Meta Mensal"
          secondaryValue="82%"
          progress={82}
          progressColor="bg-emerald-600"
        />
        <MetricCard
          title="Receita Anual"
          value={formatCurrency(Number(overview?.totalRevenue || 0))}
          sub={`Acumulado YTD ${new Date().getFullYear()}`}
          icon={TrendingUp}
          trend="+8%"
          trendUp={true}
          bg="bg-slate-100/80"
          color="text-[#0F172A]"
          secondaryLabel="Alcance Anual"
          secondaryValue="75%"
          progress={75}
          progressColor="bg-[#0F172A]"
        />
        <MetricCard
          title="Custos Mensais"
          value={formatCurrency(Number(overview?.totalExpenses || 0))}
          sub="Despesas e infraestrutura"
          icon={CreditCard}
          trend="-2%"
          trendUp={false}
          bg="bg-rose-50/80"
          color="text-rose-700"
          secondaryLabel="Orçamento utilizado"
          secondaryValue="42%"
          progress={42}
          progressColor="bg-rose-600"
        />
        <MetricCard
          title="Lucro Líquido"
          value={formatCurrency(Number(overview?.netProfit || 0))}
          sub="Receita - despesas"
          icon={TrendingUp}
          trend={Number(overview?.netProfit || 0) >= 0 ? '+5%' : '-3%'}
          trendUp={Number(overview?.netProfit || 0) >= 0}
          bg="bg-blue-50/80"
          color="text-blue-700"
          secondaryLabel="Margem"
          secondaryValue={`${Number(overview?.totalRevenue || 0) > 0 ? Math.round((Number(overview?.netProfit || 0) / Number(overview?.totalRevenue || 1)) * 100) : 0}%`}
          progress={Number(overview?.totalRevenue || 0) > 0 ? Math.round((Number(overview?.netProfit || 0) / Number(overview?.totalRevenue || 1)) * 100) : 0}
          progressColor="bg-blue-600"
        />
        <MetricCard
          title="Faturas Abertas"
          value={String(overview?.overdueInvoices || 0)}
          sub="Vencidas"
          icon={AlertCircle}
          bg="bg-amber-50/80"
          color="text-amber-700"
          secondaryLabel="Total"
          secondaryValue={String(overview?.totalInvoices || 0)}
          progress={Number(overview?.totalInvoices || 0) > 0 ? Math.round((Number(overview?.overdueInvoices || 0) / Number(overview?.totalInvoices || 1)) * 100) : 0}
          progressColor="bg-amber-500"
        />
      </section>

      {filteredEntries.length === 0 ? (
        <EmptyState title="Nenhum lançamento encontrado" description="Crie uma nova fatura, receita ou despesa para começar." onCreate={() => setIsModalOpen(true)} />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar lançamento..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-1.5 text-xs font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:bg-white transition-all"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Data</th>
                  <th className="px-4 py-2 font-medium">Descrição</th>
                  <th className="px-4 py-2 font-medium">Categoria</th>
                  <th className="px-4 py-2 font-medium">Empresa</th>
                  <th className="px-4 py-2 font-medium text-right">Valor</th>
                  <th className="px-4 py-2 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEntries.map((row: any) => (
                  <tr key={row.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-2 text-slate-600">{new Date(row.date || Date.now()).toLocaleDateString('pt-BR')}</td>
                    <td className="px-4 py-2 text-slate-800 font-medium">{row.description}</td>
                    <td className="px-4 py-2 text-slate-600">{row.category}</td>
                    <td className="px-4 py-2 text-slate-600">{row.company}</td>
                    <td className={`px-4 py-2 text-right font-medium ${row.type === 'RECEITA' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {row.type === 'RECEITA' ? '+' : '-'} {formatCurrency(Number(row.amount))}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${row.status === 'PAID' || row.status === 'PAGO' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {row.status === 'PAID' || row.status === 'PAGO' ? 'Liquidado' : row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <FinanceEntryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entry={editingEntry}
        onCreate={async (data) => {
          if (data.type === 'RECEITA') await createRevenueMutation.mutateAsync(data);
          else await createExpenseMutation.mutateAsync(data);
          setIsModalOpen(false);
        }}
        onUpdate={async (id, data) => {
          const entry = entries.find((e: any) => e.id === id);
          const entityType = entry?.entityType || 'invoice';
          if (entityType === 'invoice') await updateInvoiceMutation.mutateAsync({ id, data });
          else if (entityType === 'expense') await updateExpenseMutation.mutateAsync({ id, data });
          else if (entityType === 'revenue') await updateRevenueMutation.mutateAsync({ id, data });
          else await updateInvoiceMutation.mutateAsync({ id, data });
        }}
        onDelete={async (id) => {
          const entry = entries.find((e: any) => e.id === id);
          const entityType = entry?.entityType || 'invoice';
          if (entityType === 'invoice') await deleteInvoiceMutation.mutateAsync(id);
          else if (entityType === 'expense') await deleteExpenseMutation.mutateAsync(id);
          else if (entityType === 'revenue') await deleteRevenueMutation.mutateAsync(id);
          else await deleteInvoiceMutation.mutateAsync(id);
        }}
      />
    </div>
  );
}
